import {
	Banknote,
	History,
	MoreHorizontal,
	SearchIcon,
	SlidersHorizontal,
	XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApptPagination from "@/components/admin-portal/appointment/pagination-list";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { cn, formatCurrency } from "@/lib/utils";
import type {
	IncentiveLedgerEntry,
	IncentiveSummary,
} from "@/types/admin-portal/bonus-ro";
import type { ExternalProp } from "@/types/admin-portal/routing-mode";
import { AdjustmentDialog } from "./adjustment-dialog";
import { DEFAULT_LIMIT, limitParam, pageParam, visit } from "./balances";
import { type ActiveFilter, FilterPanel } from "./filter-panel";
import { LedgerDrawer } from "./ledger-drawer";
import { LoadFailed, Loading } from "./load-state";
import { PayoutDialog } from "./payout-dialog";
import { TabNote } from "./tab-note";

// The balances filters with the values the backend accepts; labels live in bonus-ro.json.
const FILTERS = [
	{ key: "balance", values: ["POSITIVE", "ZERO", "NEGATIVE"] },
	{ key: "has_pending", values: ["true", "false"] },
	{ key: "has_voucher", values: ["true", "false"] },
	{ key: "has_paid_out", values: ["true", "false"] },
	{ key: "has_this_month", values: ["true", "false"] },
] as const;
const ANY = "any";

type TherapistFilterKey = (typeof FILTERS)[number]["key"];

export interface TherapistsTabProps {
	therapists?: ExternalProp<IncentiveSummary[]>;
	therapistSearch: string;
	therapistFilters: Partial<Record<TherapistFilterKey, string>>;
	ledger?: ExternalProp<IncentiveLedgerEntry[]> | null;
	routes: { adjustments: string; payouts: string };
}

const CLOSED_LEDGER = {
	ledger: undefined,
	ledger_page: undefined,
	ledger_limit: undefined,
};

export function TherapistsTab({
	therapists,
	therapistSearch,
	therapistFilters,
	ledger,
	routes,
}: TherapistsTabProps) {
	const { t } = useTranslation("bonus-ro");
	const [search, setSearch] = useState(therapistSearch);
	const debouncedSearch = useDebouncedValue(search.trim(), 400);
	const [adjusting, setAdjusting] = useState<IncentiveSummary | null>(null);
	const [paying, setPaying] = useState<IncentiveSummary | null>(null);
	// ?ledger= keeps the drawer open across a refresh or a shared link.
	const [ledgerFor, setLedgerFor] = useState<{
		id: string;
		name?: string;
	} | null>(() => {
		const id = new URLSearchParams(window.location.search).get("ledger");
		return id ? { id } : null;
	});
	// Until the opened therapist's first page lands, the previous one's rows stay hidden.
	const [ledgerReady, setLedgerReady] = useState(true);

	useEffect(() => {
		if (debouncedSearch === therapistSearch.trim()) return;
		visit({ search: debouncedSearch, page: undefined }, [
			"therapists",
			"therapistSearch",
		]);
	}, [debouncedSearch, therapistSearch]);

	const applyFilters = (patch: Partial<Record<TherapistFilterKey, string>>) =>
		visit({ ...patch, page: undefined }, ["therapists", "therapistFilters"]);

	const activeFilters: ActiveFilter[] = [
		...(therapistSearch.trim()
			? [
					{
						key: "search",
						label: t("therapists.search_label"),
						value: therapistSearch.trim(),
						clear: () => setSearch(""),
					},
				]
			: []),
		...FILTERS.flatMap(({ key }) => {
			const value = therapistFilters[key];
			return value
				? [
						{
							key,
							label: t(`therapists.filters.${key}.label`),
							value: t(`therapists.filters.${key}.${value}`),
							clear: () => applyFilters({ [key]: undefined }),
						},
					]
				: [];
		}),
	];

	function clearFilters() {
		setSearch("");
		if (Object.keys(therapistFilters).length > 0) {
			applyFilters(
				Object.fromEntries(FILTERS.map(({ key }) => [key, undefined])),
			);
		}
	}

	function openLedger(therapist: IncentiveSummary) {
		setLedgerFor({ id: therapist.therapistId, name: therapist.therapistName });
		setLedgerReady(false);
		visit({ ...CLOSED_LEDGER, ledger: therapist.therapistId }, ["ledger"], () =>
			setLedgerReady(true),
		);
	}

	function closeLedger() {
		setLedgerFor(null);
		visit(CLOSED_LEDGER, ["ledger"]);
	}

	const rows = therapists?.data ?? [];
	const page = therapists?.meta?.page ?? 1;
	const limit = therapists?.meta?.limit ?? DEFAULT_LIMIT;
	const pages = therapists?.meta?.totalPages ?? 1;
	const from = (page - 1) * limit + 1;
	const goTo = (nextPage: number, nextLimit = limit) =>
		visit({ page: pageParam(nextPage), limit: limitParam(nextLimit) }, [
			"therapists",
		]);

	return (
		<div className="flex flex-col gap-4">
			<TabNote>{t("therapists.description")}</TabNote>

			<FilterPanel
				activeFilters={activeFilters}
				canClear={!!search || activeFilters.length > 0}
				onClearAll={clearFilters}
			>
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-3 lg:grid-cols-4">
					<div className="grid gap-2 md:col-span-2">
						<Label className="text-xs text-muted-foreground">
							{t("therapists.search_label")}
						</Label>
						<Input
							className="bg-input shadow-inner"
							value={search}
							placeholder={t("therapists.search")}
							aria-label={t("therapists.search")}
							StartIcon={{ icon: SearchIcon }}
							isLoading={debouncedSearch !== therapistSearch.trim()}
							EndIcon={
								search
									? {
											icon: XIcon,
											isButton: true,
											handleOnClick: () => setSearch(""),
										}
									: undefined
							}
							onChange={(e) => setSearch(e.target.value)}
						/>
					</div>
					{FILTERS.map(({ key, values }) => (
						<div key={key} className="grid gap-2">
							<Label className="text-xs text-muted-foreground">
								{t(`therapists.filters.${key}.label`)}
							</Label>
							<Select
								value={therapistFilters[key] ?? ANY}
								onValueChange={(value) =>
									applyFilters({ [key]: value === ANY ? undefined : value })
								}
							>
								<SelectTrigger className="bg-input">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value={ANY}>
										{t("therapists.filters.any")}
									</SelectItem>
									{values.map((value) => (
										<SelectItem key={value} value={value}>
											{t(`therapists.filters.${key}.${value}`)}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					))}
				</div>
			</FilterPanel>

			{!therapists ? (
				<Loading />
			) : therapists.error ? (
				<LoadFailed
					title={t("failed.therapists")}
					error={therapists.error}
					only="therapists"
				/>
			) : rows.length === 0 ? (
				<div className="flex flex-col items-center gap-3 rounded-lg border border-dashed p-10 text-center text-sm text-muted-foreground">
					{activeFilters.length > 0
						? t("therapists.no_match")
						: t("therapists.empty")}
					{/* A page past the end (an old link) has no pager to leave by. */}
					{page > 1 && (
						<Button variant="outline" size="sm" onClick={() => goTo(1)}>
							{t("therapists.first_page")}
						</Button>
					)}
				</div>
			) : (
				<>
					<div className="overflow-x-auto rounded-lg border">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("therapists.columns.therapist")}</TableHead>
									<TableHead>{t("therapists.columns.voucher")}</TableHead>
									<TableHead className="text-right">
										{t("therapists.columns.pending")}
									</TableHead>
									<TableHead className="text-right">
										{t("therapists.columns.balance")}
									</TableHead>
									<TableHead className="text-right">
										{t("therapists.columns.this_month")}
									</TableHead>
									<TableHead className="text-right">
										{t("therapists.columns.paid_out")}
									</TableHead>
									<TableHead />
								</TableRow>
							</TableHeader>
							<TableBody>
								{rows.map((row) => (
									<TableRow key={row.therapistId}>
										<TableCell className="font-medium">
											<button
												type="button"
												className="text-left underline-offset-4 hover:underline"
												onClick={() => openLedger(row)}
											>
												{row.therapistName ?? row.therapistId.slice(0, 8)}
											</button>
										</TableCell>
										<TableCell className="font-mono text-xs">
											{row.voucherCode ?? "—"}
										</TableCell>
										<TableCell className="text-right text-nowrap tabular-nums">
											{formatCurrency(row.pending)}
											<span className="block text-xs text-muted-foreground">
												{t("therapists.pending_count", {
													count: row.pendingCount,
												})}
											</span>
										</TableCell>
										<TableCell
											className={cn(
												"text-right text-nowrap font-medium tabular-nums",
												row.balance < 0 && "text-destructive",
											)}
										>
											{formatCurrency(row.balance)}
										</TableCell>
										<TableCell className="text-right text-nowrap tabular-nums">
											{formatCurrency(row.thisMonth)}
										</TableCell>
										<TableCell className="text-right text-nowrap tabular-nums">
											{formatCurrency(row.paidOut)}
										</TableCell>
										<TableCell className="text-right">
											<DropdownMenu>
												<DropdownMenuTrigger asChild>
													<Button
														variant="ghost"
														size="icon"
														className="size-8 border shadow-none"
														aria-label={t("therapists.actions", {
															name: row.therapistName,
														})}
													>
														<MoreHorizontal className="size-4" />
													</Button>
												</DropdownMenuTrigger>
												<DropdownMenuContent align="end">
													<DropdownMenuItem onSelect={() => openLedger(row)}>
														<History />
														{t("therapists.history")}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => setAdjusting(row)}>
														<SlidersHorizontal />
														{t("therapists.adjust")}
													</DropdownMenuItem>
													<DropdownMenuItem onSelect={() => setPaying(row)}>
														<Banknote />
														{t("therapists.pay_out")}
													</DropdownMenuItem>
												</DropdownMenuContent>
											</DropdownMenu>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
					<ApptPagination
						metadata={{
							page,
							pages,
							limit,
							count: therapists.meta?.totalItems ?? rows.length,
							from,
							to: from + rows.length - 1,
						}}
						label={t("therapists.pagination_label")}
						actions={{
							goToPrevpage: () => goTo(page - 1),
							goToNextPage: () => goTo(page + 1),
							goToPage: (value) => goTo(Number(value)),
							goToFirstPage: () => goTo(1),
							goToLastPage: () => goTo(pages),
							onChangeLimit: (value) => goTo(1, Number(value)),
						}}
					/>
				</>
			)}

			{adjusting && (
				<AdjustmentDialog
					therapist={adjusting}
					url={routes.adjustments}
					onClose={() => setAdjusting(null)}
				/>
			)}
			{paying && (
				<PayoutDialog
					therapist={paying}
					url={routes.payouts}
					onClose={() => setPaying(null)}
				/>
			)}
			<LedgerDrawer
				open={ledgerFor !== null}
				therapistName={
					ledgerFor?.name ??
					rows.find((r) => r.therapistId === ledgerFor?.id)?.therapistName
				}
				ledger={ledgerReady ? ledger : null}
				onClose={closeLedger}
				onPage={(nextPage, nextLimit) =>
					visit(
						{
							ledger_page: pageParam(nextPage),
							ledger_limit: limitParam(nextLimit),
						},
						["ledger"],
					)
				}
			/>
		</div>
	);
}
